
import axios from 'axios';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';

// Create API instance with base URL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  // Add a shorter timeout for quicker error feedback
  timeout: 8000,
});

// Track connection state
let hasShownConnectionError = false;
let isBackendAvailable = true;
let connectionRetryCount = 0;
let lastConnectionAttempt = 0;
const MAX_CONNECTION_RETRIES = 3;
const CONNECTION_RETRY_COOLDOWN = 5000; // 5 seconds between retries

// Add request interceptor to include auth token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add timestamp to prevent request caching issues
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: new Date().getTime()
      };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration and connection issues
api.interceptors.response.use(
  (response) => {
    // If we get a successful response, mark the backend as available
    if (!isBackendAvailable) {
      isBackendAvailable = true;
      connectionRetryCount = 0;
      // If we previously showed a connection error, show a success message
      if (hasShownConnectionError) {
        toast.success('Connection to server restored', {
          description: 'Real data is now being loaded from database'
        });
        hasShownConnectionError = false;
      }
    }
    return response;
  },
  (error) => {
    // Handle connection errors
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      isBackendAvailable = false;
      console.error('Network error:', error);
      
      const now = new Date().getTime();
      // Only show toast if we haven't shown one recently (avoid toast spam)
      if (!hasShownConnectionError && (now - lastConnectionAttempt > CONNECTION_RETRY_COOLDOWN)) {
        lastConnectionAttempt = now;
        
        // Show error message unless in development with mock data enabled
        if (!shouldUseMockData()) {
          toast.error('Cannot connect to server', {
            description: 'Check your internet connection or try again later.',
            duration: 6000,
          });
          hasShownConnectionError = true;
        } else {
          console.warn('Using mock data due to connection issue');
        }
      }
    }
    else if (error.response && error.response.status === 503) {
      // Special handling for database unavailable status
      toast.error('Database service unavailable', {
        description: 'The database is currently unavailable. We\'re attempting to reconnect.',
        duration: 6000,
      });
    }
    else if (error.response && error.response.status === 401) {
      // Unauthorized - token expired or invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('admin_user');
      
      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Function to check if the backend is available with retries
export const checkBackendConnection = async (retries = 0): Promise<boolean> => {
  try {
    // For Supabase-integrated apps, check Supabase connection first
    try {
      const { data, error } = await supabase.from('doctors').select('id').limit(1);
      if (!error) {
        isBackendAvailable = true;
        return true;
      }
    } catch (supabaseError) {
      console.warn('Supabase connection check failed, falling back to API:', supabaseError);
    }
    
    // Use a simple GET request to check connectivity
    // Adding a unique parameter to prevent caching
    const timestamp = new Date().getTime();
    const response = await api.get(`/test?_=${timestamp}`, { timeout: 3000 });
    // Verify that we not only got a response but the database is connected
    return response.data && response.data.message === 'API and database connection are working!';
  } catch (error) {
    console.error('Backend connection check failed:', error);
    
    // Implement retry logic
    if (retries < MAX_CONNECTION_RETRIES) {
      console.info(`Retrying connection check (${retries + 1}/${MAX_CONNECTION_RETRIES})...`);
      // Wait for 2 seconds before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 2000 * Math.pow(2, retries)));
      return checkBackendConnection(retries + 1);
    }
    
    return false;
  }
};

// In production, we never use mock data unless specifically configured
export const shouldUseMockData = (): boolean => {
  // Always return false for production unless explicitly overridden
  if (import.meta.env.PROD && import.meta.env.VITE_USE_MOCK_DATA !== 'true') {
    return false;
  }
  
  // In development, check env var
  return import.meta.env.VITE_USE_MOCK_DATA === 'true';
};

// Check if connection is available for a specific route
export const isConnectionAvailable = (): boolean => {
  return isBackendAvailable;
};

// Reset connection error flag (useful when manually triggering a retry)
export const resetConnectionError = (): void => {
  hasShownConnectionError = false;
};

export default api;
